import { getNavigation, getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import { SeasonFragment, SeasonLeagueFragment } from '@amf/shared/types/graphql';
import { useTranslate } from '@tolgee/react';
import { useEffect, useState } from 'react';
import TeamTableFilter, { TeamTableFilterData } from '@amf/shared/components/team/TeamTableFilter';
import { LeagueTeamHomeOrAway } from '@amf/shared/data/League';
import SeasonHeader from '@amf/shared/components/season/SeasonHeader';
import Menu from '@amf/shared/components/menu/Menu';
import Container from '@amf/shared/components/layout/Container';
import Empty from '@amf/shared/components/utils/Empty';
import TeamTable from '@amf/shared/components/team/TeamTable';
import { GetServerSideProps } from 'next';
import { sdk } from '@amf/shared/lib/graphQlClient';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import usePersistentState from '@amf/shared/hooks/usePersistentState';

export interface TablePageProps extends NavigationProps {
  seasons: SeasonFragment[];
}

type Props = {
  seasons: SeasonFragment[];
  links: { team: string; match: string };
  disableWonInExtension?: boolean;
};

function TablePage({ seasons, links, disableWonInExtension }: Props) {
  const { t } = useTranslate();
  const [season, setSeason] = usePersistentState<SeasonFragment>(seasons, 'season');
  const [league, setLeague] = usePersistentState<SeasonLeagueFragment | undefined>(
    season?.leagues ?? [],
    'league',
  );
  const [filter, setFilter] = useState<TeamTableFilterData | undefined>();

  useEffect(() => {
    const phase = league?.phases.find(phase => phase.current) ?? league?.phases[0];
    setFilter({ phase: phase?.id, homeOrAway: LeagueTeamHomeOrAway.all });
  }, [league]);

  if (!season) return null;
  return (
    <>
      <SeasonHeader
        title={t('table.list.title')}
        seasons={seasons}
        selected={season}
        onSeasonChange={season => {
          setSeason(season);
          setLeague(season.leagues[0]);
        }}
      />
      {league ? (
        <>
          <Menu<SeasonLeagueFragment>
            scrollable
            label={t('seasonLeague.label')}
            onChange={({ value }) => setLeague(value)}
            selected={{ id: league.id, title: league.name, value: league }}
            items={season.leagues.map(league => ({
              id: league.id,
              title: league.name,
              value: league,
            }))}
          />
          {league.phases.length === 0 ? (
            <Container>
              <Empty>{t('table.list.noPhases')}</Empty>
            </Container>
          ) : (
            <>
              <Container>
                <TeamTableFilter
                  league={league}
                  onChange={data =>
                    setFilter({
                      phase: data.phase.length === 0 ? null : data.phase,
                      homeOrAway: data.homeOrAway,
                    })
                  }
                />
                <TeamTable
                  filter={filter}
                  league={league}
                  prefixes={links}
                  disableWonInExtension={disableWonInExtension}
                />
              </Container>
            </>
          )}
        </>
      ) : (
        <Container>
          <Empty>{t('table.list.noLeagues')}</Empty>
        </Container>
      )}
    </>
  );
}

export default TablePage;

export const getServerSideProps =
  (): GetServerSideProps<TablePageProps> =>
  async ({ locale, res }) => {
    res.setHeader('Cache-Control', 'max-age=0, s-maxage=10');
    const { seasons } = await sdk.SeasonList({ union: process.env.NEXT_PUBLIC_UNION });
    if (seasons.length === 0) return { notFound: true };

    return {
      props: {
        seasons,
        navigation: await getNavigation(getLanguageLocale(locale!)),
        partners: await getPartners(),
      },
    };
  };
